/**
 * MTNPK Glass Store Locator
 * Google Maps integration and interactive map functionality
 */

(function() {
  'use strict';

  class StoreLocatorMap {
    constructor(sectionId, locations, apiKey) {
      this.sectionId = sectionId;
      this.locations = locations || [];
      this.apiKey = apiKey;
      this.map = null;
      this.markers = [];
      this.infoWindow = null;
      this.bounds = null;
    }

    /**
     * Initialize the map
     */
    init() {
      if (!this.locations.length) {
        console.warn('No locations to display');
        return;
      }

      const mapContainer = document.getElementById(`store-map-${this.sectionId}`);
      if (!mapContainer) {
        console.warn('Map container not found');
        return;
      }

      // Calculate bounds from all locations
      this.bounds = this.calculateBounds();

      // Initialize Google Map
      this.map = new google.maps.Map(mapContainer, {
        zoom: 5,
        center: this.bounds.getCenter(),
        mapTypeControl: false,
        fullscreenControl: true,
        zoomControl: true,
        streetViewControl: false,
        mapId: '96f4c7dcd339db8733fe821f',
        styles: this.getMapStyles()
      });

      // Add markers for each location
      this.addMarkers();

      // Fit bounds to all markers
      this.map.fitBounds(this.bounds, 50);

      // Setup map interactions
      this.setupMapInteractions();

      // Close info window when clicking map background
      this.map.addListener('click', () => {
        if (this.infoWindow) {
          this.infoWindow.close();
        }
      });

      console.log('Store locator map initialized with', this.locations.length, 'locations');
    }

    /**
     * Calculate bounding box from all locations
     */
    calculateBounds() {
      const bounds = new google.maps.LatLngBounds();

      this.locations.forEach(location => {
        bounds.extend({
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude)
        });
      });

      return bounds;
    }

    /**
     * Add markers to map
     */
    addMarkers() {
      this.locations.forEach((location, index) => {
        this.createMarker(location, index);
      });
    }

    /**
     * Create individual marker
     */
    createMarker(location, index) {
      const position = {
        lat: parseFloat(location.latitude),
        lng: parseFloat(location.longitude)
      };

      // Create marker content with store details
      const markerContent = document.createElement('div');
      markerContent.className = 'store-locator__marker';
      markerContent.textContent = '📍';
      markerContent.style.fontSize = '32px';
      markerContent.style.cursor = 'pointer';

      // Use AdvancedMarkerElement if available (newer API)
      if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: position,
          map: this.map,
          title: location.name,
          content: markerContent
        });

        // Use gmp-click event for Advanced Markers
        marker.addEventListener('gmp-click', () => {
          this.showInfoWindow(location, marker);
          this.highlightLocation(index);
          this.map.panTo(position);
        });

        this.markers.push({
          marker: marker,
          location: location,
          index: index,
          content: markerContent
        });
      } else {
        // Fallback to basic Marker
        const marker = new google.maps.Marker({
          position: position,
          map: this.map,
          title: location.name
        });

        marker.addListener('click', () => {
          this.showInfoWindow(location, marker);
          this.highlightLocation(index);
          this.map.panTo(position);
        });

        this.markers.push({
          marker: marker,
          location: location,
          index: index
        });
      }
    }

    /**
     * Show info window for location
     */
    showInfoWindow(location, marker) {
      // Close previous info window
      if (this.infoWindow) {
        this.infoWindow.close();
      }

      const content = `
        <div class="store-locator__info-window">
          <div class="info-window__header">
            <h4 class="info-window__title">${this.escapeHtml(location.name)}</h4>
          </div>
          <div class="info-window__body">
            <p class="info-window__address">
              <strong>${this.escapeHtml(location.address)}</strong><br>
              ${this.escapeHtml(location.city)}, ${this.escapeHtml(location.province)} ${this.escapeHtml(location.postal_code)}
            </p>
            ${location.phone ? `
              <p class="info-window__phone">
                <a href="tel:${location.phone}">📞 ${location.phone}</a>
              </p>
            ` : ''}
            ${location.website ? `
              <p class="info-window__website">
                <a href="${location.website}" target="_blank" rel="noopener noreferrer">🌐 Visit Website</a>
              </p>
            ` : ''}
            <p class="info-window__directions">
              <a href="https://maps.google.com/?q=${encodeURIComponent(location.address + ' ' + location.city + ' ' + location.province)}" target="_blank" rel="noopener noreferrer">
                📍 Get Directions
              </a>
            </p>
          </div>
        </div>
      `;

      this.infoWindow = new google.maps.InfoWindow({
        content: content,
        maxWidth: 280
      });

      // For Advanced Markers, use anchor property
      if (marker.position) {
        this.infoWindow.open({
          map: this.map,
          anchor: marker,
          shouldFocus: true
        });
      } else {
        // Fallback for basic markers
        this.infoWindow.open(this.map, marker);
      }
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return text.replace(/[&<>"']/g, m => map[m]);
    }

    /**
     * Highlight location in the list
     */
    highlightLocation(index) {
      const listItems = document.querySelectorAll(`#location-list-${this.sectionId} .store-locator__location-item`);
      listItems.forEach((item, i) => {
        item.classList.remove('active');
      });

      if (listItems[index]) {
        listItems[index].classList.add('active');
        listItems[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    /**
     * Setup map interaction handlers
     */
    setupMapInteractions() {
      const listContainer = document.getElementById(`location-list-${this.sectionId}`);
      if (!listContainer) return;

      // Setup list item click handlers
      const listItems = listContainer.querySelectorAll('.store-locator__location-item');
      listItems.forEach((item, index) => {
        item.addEventListener('click', () => {
          if (this.markers[index]) {
            const markerData = this.markers[index];
            this.highlightLocation(index);
            this.showInfoWindow(markerData.location, markerData.marker);
            this.map.panTo(markerData.marker.getPosition());
          }
        });

        item.addEventListener('mouseenter', () => {
          item.classList.add('hover');
          if (this.markers[index]) {
            const markerData = this.markers[index];
            if (markerData.content) {
              markerData.content.style.transform = 'scale(1.3)';
              markerData.content.style.transition = 'transform 0.2s ease';
            }
          }
        });

        item.addEventListener('mouseleave', () => {
          item.classList.remove('hover');
          if (this.markers[index]) {
            const markerData = this.markers[index];
            if (markerData.content) {
              markerData.content.style.transform = 'scale(1)';
            }
          }
        });
      });
    }

    /**
     * Get custom map styling
     */
    getMapStyles() {
      return [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }]
        },
        {
          featureType: 'water',
          stylers: [{ color: '#e8f4f8' }]
        }
      ];
    }

    /**
     * Filter and update markers based on search
     */
    filterLocations(filtered) {
      // Clear existing markers
      this.markers.forEach(m => m.marker.setMap(null));
      this.markers = [];

      // Add new markers for filtered locations
      filtered.forEach((location, index) => {
        this.createMarker(location, index);
      });

      // Recalculate bounds
      const filteredBounds = new google.maps.LatLngBounds();
      filtered.forEach(location => {
        filteredBounds.extend({
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude)
        });
      });

      this.map.fitBounds(filteredBounds, 50);
    }

    /**
     * Reset to show all locations
     */
    resetMap() {
      this.markers.forEach(m => m.marker.setMap(null));
      this.markers = [];
      this.addMarkers();
      this.map.fitBounds(this.bounds, 50);
    }
  }

  // Export to window
  window.StoreLocatorMap = StoreLocatorMap;
})();
